import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import { toast } from 'react-toastify';
import useManageWeeklyShip from '../weeklyShip/useManageWeeklyShip';
import { formatDate } from '../../utils/dateUtils';
import WeeklyShipImageUpload from './WeeklyShipImageUpload';
import DealerParcelImportModal from './DealerParcelImportModal';
import SheetImportPanel from './SheetImportPanel';
import CircularProgress from '@mui/material/CircularProgress';


const WeeklyShipImport = () => {
  const columnDefs = [
    { headerName: 'No', field: 'ojNo', flex: 1 },
    { headerName: 'Key', field: 'key', flex: 1 },
    { headerName: 'Order Date', field: 'arrivedDate', flex: 1 },
    { headerName: 'Box Name', field: 'boxName', flex: 1 },
    { headerName: 'Tracking No.', field: 'trackingJp', flex: 1 },
    { headerName: 'COD', field: 'codPrice', flex: 1 },
    { 
      headerName: 'Weight', 
      field: 'weight', 
      flex: 1,
      cellRenderer: (params) => {
        const weight = parseFloat(params.value);
        const isInvalid = isNaN(weight) || weight > 300;
        return (
          <span style={{ 
            color: isInvalid ? 'red' : 'black',
            fontWeight: isInvalid ? 'bold' : 'normal'
          }}>
            {params.value} {isInvalid && '(เกิน 300kg)'}
          </span>
        );
      },
      cellStyle: (params) => {
        const weight = parseFloat(params.value);
        const isInvalid = isNaN(weight) || weight > 300;
        return isInvalid ? { backgroundColor: '#ffebee' } : null;
      }
    },
    { headerName: 'Box No.', field: 'boxNo', flex: 1 },
    { headerName: 'Ship Date', field: 'shipDate', flex: 1 },
    // ... add more columns as required, and remember to set flex: 1
  ];

  const [rowData, setRowData] = useState([]);
  const [shipDate, setShipDate] = useState('');
  const [shipDateId, setShipDateId] = useState('');
  const [pasteArea, setPasteArea] = useState('');
  const [dealerModalOpen, setDealerModalOpen] = useState(false);

  // Air (dealer) rounds get an extra "import dealer parcel Excel" action.
  const isDealerRound = shipDate.includes('D2U') || shipDate.includes('ODA J4410');

  const {addWeeklyShipDate, isLoading, error} = useManageWeeklyShip();


  const removeDash = (tracking) => {
    return tracking.replace(/-/g,"")
  }

  const removeSmileFromBoxName = (boxName) => {
    return boxName.replace(/smile\s*/ig, '').replace(/\s+/g, ' ').trim();
  }

  const extractNumberFromString = (inputStr) => {
    if(inputStr){
      // Strip currency symbols, commas (thousands separator) and spaces, but KEEP
      // the decimal point so "1,234.00" -> 1234, not 123400 (100x inflation).
      const numberStr = String(inputStr).replace(/[^\d.-]/g, '');
      const num = parseFloat(numberStr);
      return isNaN(num) ? 0 : Math.round(num);
    }
    return inputStr
  }

  const checkBoxNoDuplicate = (rows) => {
    const boxNoSet = new Set();
  
    for (const row of rows) {
      if (boxNoSet.has(row.boxNo)) {
        // Found a duplicate
        return row.boxNo;
      }
      if(row.boxNo === ""){
        return "there is empty"
      }
      boxNoSet.add(row.boxNo);
    }
    
    // No duplicates found
    return false;
  }

  const handleLoadClick = async () => {
    if (!shipDate) {
      toast.error("Please select Ship Date First");
    } else {
      let updateArrived = ''
      let updatedKey = ''
      const escapedPasteArea = pasteArea.replace(/"([^"]*)"/g, (match, p1) => {
        // Replace newlines within the quotes with a placeholder (e.g., \\n)
        return `"${p1.replace(/\n/g, '\\n')}"`
      });
      const rows = escapedPasteArea.split('\n').map(row => {
        const [key, arrivedDate, picture, boxName, trackingJp, codPrice, weight, boxNo] = row.split('\t');
        let ojNo = parseInt(key);
        if (!isNaN(ojNo)) {
          updatedKey = `${key}_${shipDate}`
        }else{
          if(trackingJp != 'ไม่มีเลขพัสดุ' && trackingJp != ''){
            updatedKey = `${removeDash(trackingJp)}_${shipDate}`
          }else{
            updatedKey = `box_${boxNo}_${shipDate}`
          }
          ojNo = 0
        }


        if(formatDate(arrivedDate).includes('NaN')){
          updateArrived = shipDate
        }else{
          updateArrived = formatDate(arrivedDate)
        }
        
        return {ojNo, key: updatedKey, arrivedDate: updateArrived, boxName: removeSmileFromBoxName(boxName), trackingJp: removeDash(trackingJp), codPrice: extractNumberFromString(codPrice), weight, boxNo, shipDate };
      });
      // Update the state once after the loop  
      const result = checkBoxNoDuplicate(rows)
      if (result === false) {
        setRowData(rows);
      } else if (result === "there is empty") {
        toast.error(`มีข้อมูลที่ไม่มีเลขกล่อง`)
      } else {
        toast.error(`เลขกล่องซ้ำ - ${result}`)
      }
      console.log(isLoading)
    }
  };

  const handleSave = async () => {
    // Frontend validation: Check for weights exceeding 300kg
    const invalidWeights = rowData.filter(item => {
      const weight = parseFloat(item.weight);
      return isNaN(weight) || weight > 300;
    });

    if (invalidWeights.length > 0) {
      const errorDetails = invalidWeights.map(item => 
        `BoxNo: ${item.boxNo || 'N/A'}, Weight: ${item.weight}kg`
      ).join('; ');
      toast.error(`Wrong data: Weight exceeds 300kg limit. ${errorDetails}`);
      return; // Stop execution, don't send to backend
    }

    const response = await addWeeklyShipDate({data:rowData, shipDateId})
    if(response){
      toast.success('Update records successfully')
      setRowData([])
    }else{
      if(error == 'ON CONFLICT DO UPDATE command cannot affect row a second time'){
        toast.error('Duplicate Box No.')
      }else{
        toast.error(error);
      }
    }
  }
  
  const handleSelectChange = (e) => {
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedId = e.target.value;
    setShipDate(selectedText);
    setShipDateId(selectedId);
  }

  useEffect(() => {
    if(error == 'ON CONFLICT DO UPDATE command cannot affect row a second time'){
      toast.error('Duplicate Box No.')
    }else{
      toast.error(error);
    }
  }, [error]);

  return (
    <div className="ag-theme-alpine container mx-auto" style={{ width: '100%' }}>
      <h2 className='text-5xl'>Weekly Ship Import</h2>
      <SheetImportPanel />
      <div className='grid grid-cols-3 gap-x-5'>
        <textarea
          className='col-span-2'
          placeholder="Paste your data here"
          style={{
            width: '100%',
            height: '100px',
            padding: '8px',
            margin: '10px 0',
            borderRadius: '4px',
            border: '1px solid #ccc'
          }}
          value={pasteArea}
          onChange={e => setPasteArea(e.target.value)}
          />
        <div>
          <ShipDateDropdown 
            handleDataChange={handleSelectChange}
            includeDefault={true}
            />
          <button 
            onClick={handleLoadClick}
            className='bg-blue-500 text-white px-3 py-1.5 rounded-md my-2'
            >
            Load Data
          </button>
          <button 
            onClick={handleSave}
            className='ml-3 bg-green-600 text-white px-3 py-1.5 rounded-md my-2 mr-5 '
            disabled={isLoading}
            >
            Save Data
          </button>
          {isLoading && <CircularProgress />} {/* Render spinner when loading */}
          {isDealerRound && (
            <button
              onClick={() => setDealerModalOpen(true)}
              className='block mt-3 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md'
            >
              นำเข้าพัสดุ Dealer (Excel)
            </button>
          )}
          <WeeklyShipImageUpload shipDate={shipDate} shipDateId={shipDateId}/>
        </div>
      </div>
      <DealerParcelImportModal open={dealerModalOpen} onClose={() => setDealerModalOpen(false)} shipDateId={shipDateId} />
      <div style={{ height: 300, marginTop: '10px' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          domLayout='autoHeight'
          defaultColDef={{ resizable: true }}
          // other props
        />
      </div>
    </div>
  );
}

export default WeeklyShipImport;
